import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import { Package, TrendingUp, Award } from 'lucide-react';
import { toast } from 'sonner';
import SAR from '../components/SAR';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = [
  'hsl(221, 83%, 53%)',  // blue
  'hsl(142, 76%, 36%)',  // green
  'hsl(48, 96%, 53%)',   // yellow
  'hsl(0, 84%, 60%)',    // red
  'hsl(280, 70%, 55%)',  // purple
  'hsl(25, 95%, 55%)',   // orange
  'hsl(190, 85%, 45%)',  // teal
  'hsl(330, 75%, 55%)',  // pink
  'hsl(50, 90%, 45%)',   // gold
  'hsl(165, 70%, 40%)',  // emerald
  'hsl(255, 70%, 60%)',  // indigo
  'hsl(15, 80%, 50%)',   // brick
  'hsl(200, 80%, 40%)',  // sky-blue
  'hsl(95, 60%, 45%)',   // lime
  'hsl(310, 65%, 50%)',  // magenta
];

const RADIAN = Math.PI / 180;
const renderDonutLabel = ({ cx, cy, midAngle, outerRadius, percent }) => {
  if (!percent || percent < 0.03) return null;
  const r = outerRadius + 18;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  const isRight = Math.cos(-midAngle * RADIAN) >= 0;
  const text = `${(percent * 100).toFixed(0)}%`;
  const w = text.length * 8 + 6;
  const px = isRight ? x : x - w;
  return (
    <g style={{ pointerEvents: 'none' }}>
      <rect x={px} y={y - 9} width={w} height={18} rx={9} ry={9} fill="rgba(255,255,255,0.95)" stroke="hsl(214, 32%, 91%)" strokeWidth={0.5} />
      <text
        x={x}
        y={y}
        fill="hsl(222, 47%, 11%)"
        textAnchor={isRight ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={11}
        fontWeight={700}
        dx={isRight ? 3 : -3}
      >
        {text}
      </text>
    </g>
  );
};

export default function EmployeeProductSales() {
  const { user, employee } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      if (user?.role === 'admin' || user?.role === 'supervisor') {
        const employeesRes = await axios.get(`${API_URL}/api/employees`);
        setEmployees(employeesRes.data);
        if (employeesRes.data.length > 0) {
          loadEmployeeData(employeesRes.data[0].id);
        }
      } else if (user?.employee_id) {
        loadEmployeeData(user.employee_id);
      }
    } catch (error) {
      toast.error('فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const loadEmployeeData = async (employeeId) => {
    try {
      const response = await axios.get(`${API_URL}/api/analytics/employee-product-sales/${employeeId}`);
      setData(response.data);
      setSelectedEmployee(employeeId);
    } catch (error) {
      toast.error('فشل تحميل مبيعات الموظف');
    }
  };

  if (loading) {
    return <div className="text-center py-12">جاري التحميل...</div>;
  }

  const weeklyChartData = data?.product_sales_weekly?.map(p => ({
    name: p.product_name,
    مبيعات: p.total
  })) || [];

  const monthlyChartData = data?.product_sales_monthly?.map(p => ({
    name: p.product_name,
    مبيعات: p.total
  })) || [];

  const pieChartData = data?.product_sales_all_time?.map(p => ({
    name: p.product_name,
    value: p.total
  })) || [];

  const isEmployee = user?.role === 'employee';

  return (
    <div className="space-y-6" data-testid="employee-product-sales-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-black text-primary">
          {isEmployee ? 'مبيعاتي السابقة' : 'مبيعات المنتجات'}
        </h1>
        <p className="text-muted-foreground mt-1">
          {isEmployee ? 'استعراض مبيعاتك السابقة حسب المنتج' : 'تفاصيل المبيعات حسب المنتج'}
        </p>
      </div>

      {(user?.role === 'admin' || user?.role === 'supervisor') && employees.length > 0 && (
        <Card className="p-4 border-border">
          <div className="flex flex-wrap gap-2">
            {employees.map((emp) => (
              <button
                key={emp.id}
                onClick={() => loadEmployeeData(emp.id)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  selectedEmployee === emp.id
                    ? 'bg-brand_blue text-white'
                    : 'bg-accent text-foreground hover:bg-accent/80'
                }`}
              >
                {emp.name}
              </button>
            ))}
          </div>
        </Card>
      )}

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="p-6 border-border shadow-sm">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-lg bg-brand_blue/10 flex items-center justify-center">
                  <Package className="h-6 w-6 text-brand_blue" />
                </div>
                <div>
                  <p className="text-sm font-bold text-muted-foreground">عدد المنتجات المباعة</p>
                  <p className="text-2xl font-black text-foreground font-numbers">
                    {data.total_products_sold}
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6 border-border shadow-sm">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-lg bg-success/10 flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-success" />
                </div>
                <div>
                  <p className="text-sm font-bold text-muted-foreground">المنتج الأكثر مبيعاً</p>
                  <p className="text-lg font-bold text-foreground">
                    {data.product_sales_all_time[0]?.product_name || '-'}
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6 border-border shadow-sm">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-lg bg-warning/10 flex items-center justify-center">
                  <Award className="h-6 w-6 text-warning" />
                </div>
                <div>
                  <p className="text-sm font-bold text-muted-foreground">أعلى مبيعات منتج</p>
                  <p className="text-lg font-bold text-foreground font-numbers">
                    {data.product_sales_all_time[0]?.total?.toLocaleString('en-US') || 0} <SAR />
                  </p>
                </div>
              </div>
            </Card>
          </div>

          <Tabs defaultValue="weekly" className="w-full">
            <TabsList className="grid w-full grid-cols-3 max-w-md">
              <TabsTrigger value="weekly">أسبوعي</TabsTrigger>
              <TabsTrigger value="monthly">شهري</TabsTrigger>
              <TabsTrigger value="alltime">إجمالي</TabsTrigger>
            </TabsList>

            <TabsContent value="weekly" className="space-y-6 mt-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="p-6 border-border">
                  <h3 className="text-lg sm:text-xl font-bold text-primary mb-6">مبيعات الأسبوع حسب المنتج</h3>
                  {weeklyChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={weeklyChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 32%, 91%)" />
                        <XAxis dataKey="name" tick={{ fill: 'hsl(222, 47%, 11%)', fontSize: 12 }} />
                        <YAxis tick={{ fill: 'hsl(222, 47%, 11%)', fontSize: 12 }} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'hsl(0, 0%, 100%)',
                            border: '1px solid hsl(214, 32%, 91%)',
                            borderRadius: '8px',
                            direction: 'rtl'
                          }}
                        />
                        <Bar dataKey="مبيعات" fill="hsl(142, 76%, 36%)" radius={[8, 8, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-center text-muted-foreground py-12">لا توجد مبيعات هذا الأسبوع</p>
                  )}
                </Card>

                <Card className="p-6 border-border">
                  <h3 className="text-lg sm:text-xl font-bold text-primary mb-6">تفاصيل المنتجات - أسبوعي</h3>
                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                    {data.product_sales_weekly.length > 0 ? (
                      data.product_sales_weekly.map((product, index) => (
                        <div key={product.product_id} className="flex items-center justify-between p-3 bg-accent/30 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-success/10 flex items-center justify-center">
                              <span className="font-bold text-success">{index + 1}</span>
                            </div>
                            <div>
                              <p className="font-bold text-foreground">{product.product_name}</p>
                              <p className="text-xs text-muted-foreground">
                                {product.count} معاملة
                              </p>
                            </div>
                          </div>
                          <p className="text-lg font-bold text-success font-numbers">
                            {product.total.toLocaleString('en-US')} <SAR />
                          </p>
                        </div>
                      ))
                    ) : (
                      <p className="text-center text-muted-foreground py-8">لا توجد مبيعات</p>
                    )}
                  </div>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="monthly" className="space-y-6 mt-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="p-6 border-border">
                  <h3 className="text-lg sm:text-xl font-bold text-primary mb-6">مبيعات الشهر حسب المنتج</h3>
                  {monthlyChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={monthlyChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 32%, 91%)" />
                        <XAxis dataKey="name" tick={{ fill: 'hsl(222, 47%, 11%)', fontSize: 12 }} />
                        <YAxis tick={{ fill: 'hsl(222, 47%, 11%)', fontSize: 12 }} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'hsl(0, 0%, 100%)',
                            border: '1px solid hsl(214, 32%, 91%)',
                            borderRadius: '8px',
                            direction: 'rtl'
                          }}
                        />
                        <Bar dataKey="مبيعات" fill="hsl(221, 83%, 53%)" radius={[8, 8, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-center text-muted-foreground py-12">لا توجد مبيعات هذا الشهر</p>
                  )}
                </Card>

                <Card className="p-6 border-border">
                  <h3 className="text-lg sm:text-xl font-bold text-primary mb-6">تفاصيل المنتجات - شهري</h3>
                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                    {data.product_sales_monthly.length > 0 ? (
                      data.product_sales_monthly.map((product, index) => (
                        <div key={product.product_id} className="flex items-center justify-between p-3 bg-accent/30 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-brand_blue/10 flex items-center justify-center">
                              <span className="font-bold text-brand_blue">{index + 1}</span>
                            </div>
                            <div>
                              <p className="font-bold text-foreground">{product.product_name}</p>
                              <p className="text-xs text-muted-foreground">
                                {product.count} معاملة
                              </p>
                            </div>
                          </div>
                          <p className="text-lg font-bold text-brand_blue font-numbers">
                            {product.total.toLocaleString('en-US')} <SAR />
                          </p>
                        </div>
                      ))
                    ) : (
                      <p className="text-center text-muted-foreground py-8">لا توجد مبيعات</p>
                    )}
                  </div>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="alltime" className="space-y-6 mt-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="p-6 border-border">
                  <h3 className="text-lg sm:text-xl font-bold text-primary mb-6">إجمالي المبيعات حسب المنتج</h3>
                  {pieChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={360}>
                      <PieChart margin={{ top: 16, right: 36, bottom: 8, left: 36 }}>
                        <Pie
                          data={pieChartData}
                          cx="50%"
                          cy="42%"
                          labelLine={false}
                          label={renderDonutLabel}
                          outerRadius={75}
                          innerRadius={40}
                          paddingAngle={2}
                          fill="#8884d8"
                          dataKey="value"
                          isAnimationActive={false}
                        >
                          {pieChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="#fff" strokeWidth={2} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value, name) => [`${Number(value).toLocaleString('en-US')} ر.س`, name]}
                          contentStyle={{
                            backgroundColor: 'hsl(0, 0%, 100%)',
                            border: '1px solid hsl(214, 32%, 91%)',
                            borderRadius: '8px',
                            direction: 'rtl',
                            fontFamily: 'inherit',
                          }}
                        />
                        <Legend
                          verticalAlign="bottom"
                          align="center"
                          iconType="circle"
                          wrapperStyle={{ direction: 'rtl', fontSize: '12px', paddingTop: '12px' }}
                          formatter={(value) => <span style={{ color: 'hsl(222, 47%, 11%)', marginRight: 4 }}>{value}</span>}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-center text-muted-foreground py-12">لا توجد مبيعات</p>
                  )}
                </Card>

                <Card className="p-6 border-border">
                  <h3 className="text-lg sm:text-xl font-bold text-primary mb-6">تفاصيل جميع المنتجات</h3>
                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                    {data.product_sales_all_time.length > 0 ? (
                      data.product_sales_all_time.map((product, index) => (
                        <div key={product.product_id} className="flex items-center justify-between p-3 bg-accent/30 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div 
                              className="h-10 w-10 rounded-full flex items-center justify-center"
                              style={{ backgroundColor: `${COLORS[index % COLORS.length]}15` }}
                            >
                              <span className="font-bold" style={{ color: COLORS[index % COLORS.length] }}>
                                {index + 1}
                              </span>
                            </div>
                            <div>
                              <p className="font-bold text-foreground">{product.product_name}</p>
                              <p className="text-xs text-muted-foreground">
                                {product.count} معاملة
                              </p>
                            </div>
                          </div>
                          <p className="text-lg font-bold font-numbers" style={{ color: COLORS[index % COLORS.length] }}>
                            {product.total.toLocaleString('en-US')} <SAR />
                          </p>
                        </div>
                      ))
                    ) : (
                      <p className="text-center text-muted-foreground py-8">لا توجد مبيعات</p>
                    )}
                  </div>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
